import React from 'react';
import { Link } from 'react-router-dom';

// External link helper
const Ext = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-indigo-600 font-semibold hover:underline"
  >
    {children}
  </a>
);

const Int = ({ to, children }) => (
  <Link to={to} className="text-indigo-600 font-semibold hover:underline">
    {children}
  </Link>
);

// CTA block helper -- used inline between article sections
const cta = (heading, label, href) => ({ type: 'cta', heading, label, href });

export const ARTICLES = [
  {
    slug: 'what-is-a-3pl',
    category: 'Fundamentals',
    date: 'Feb 7, 2026',
    readTime: '7 min read',
    title: 'What Is a 3PL? A Plain-English Guide for Ecommerce Founders',
    excerpt:
      "If you've ever Googled “3PL” and walked away more confused than when you started, this one's for you. A jargon-free walkthrough of what a third-party logistics partner actually does.",
    blocks: [
      {
        type: 'p',
        children: (
          <>
            “3PL” gets thrown around like everyone agrees on what it
            means. They don’t. The same word covers everything from a
            single-warehouse operation that prints shipping labels to global
            logistics platforms with hundreds of locations. So before signing a
            contract or building a fulfillment plan around one, it helps to
            level-set what you’re actually getting.
          </>
        ),
      },
      { type: 'h2', text: 'The simple definition' },
      {
        type: 'p',
        children: (
          <>
            A 3PL (third-party logistics provider) is a company that holds your
            inventory, picks and packs orders when they come in, and ships them
            to your customer. You stay in charge of the brand, the product, and
            the marketing; the 3PL handles the physical work of getting the
            order out the door. The{' '}
            <Ext href="https://en.wikipedia.org/wiki/Third-party_logistics">
              broader Wikipedia entry
            </Ext>{' '}
            is fine if you want the academic version, but for an ecommerce
            brand the practical definition is: <strong>they ship your stuff</strong>.
          </>
        ),
      },
      { type: 'h2', text: 'What a 3PL actually does' },
      {
        type: 'p',
        text: 'A typical day-to-day looks like this:',
      },
      {
        type: 'ul',
        items: [
          'Receives your inbound inventory at a dock door, counts it, and puts it on a shelf or pallet.',
          'Connects to your storefront (Shopify, Amazon, TikTok Shop, etc.) so orders flow in automatically.',
          'Picks the items, packs them in your branded packaging, prints the label, and tenders the parcel to UPS / FedEx / USPS.',
          'Sends tracking back to the storefront so the customer gets a confirmation email.',
          'Processes returns when packages come back.',
        ],
      },
      {
        type: 'p',
        children: (
          <>
            That’s the core. Everything beyond that — kitting,
            subscription assembly, B2B routing, custom packaging, FBA prep —
            is layered on top of those four moves.
          </>
        ),
      },
      cta(
        'Curious what fulfillment with no minimums and no contracts looks like?',
        'View Pricing',
        '/#pricing'
      ),
      { type: 'h2', text: 'When does a 3PL make sense?' },
      {
        type: 'p',
        text:
          'Most brands don’t outsource fulfillment because they want to. They outsource because the math finally tips. Common triggers:',
      },
      {
        type: 'ul',
        items: [
          'You’re shipping more than 200–300 orders per month and packing them yourself is killing 1–2 days a week.',
          'You’re running out of physical space at home or in a co-working setup.',
          'You’re scaling into wholesale or retail and need pallet receiving, ASN, and routing-guide compliance.',
          'You’re losing money on shipping because you don’t have negotiated carrier rates.',
          'You want to launch in a new geography but don’t want to staff a second warehouse.',
        ],
      },
      { type: 'h2', text: 'What a 3PL is NOT' },
      {
        type: 'p',
        children: (
          <>
            A 3PL is not a freight forwarder, not a customs broker, and not a
            marketing agency. They handle the order → doorstep moment.
            Everything before the warehouse (manufacturing, freight, customs)
            and everything after the box arrives (returns policy decisions,
            customer support tone, post-purchase emails) is still on you.
          </>
        ),
      },
      { type: 'h2', text: 'The bottom line' },
      {
        type: 'p',
        children: (
          <>
            A 3PL is the operational extension of your team. The good ones make
            you faster, cheaper, and more flexible. The bad ones become the
            single biggest source of customer complaints in your inbox.{' '}
            <Int to="/about">Pick a partner</Int> who treats your brand like
            their brand and you’ll never look back.
          </>
        ),
      },
      cta(
        'Want to see what shipping from Weston, FL would cost your brand?',
        'Get Instant Estimate',
        '/zones#instant-estimate'
      ),
    ],
  },

  {
    slug: 'shipping-zones-101',
    category: 'Shipping',
    date: 'Mar 12, 2026',
    readTime: '8 min read',
    title: 'Shipping Zones 101: Why Your 3PL’s ZIP Code Affects Your Margin',
    excerpt:
      'Two warehouses. Same carrier. Same package. Wildly different shipping costs. Here’s why — and what to do about it.',
    blocks: [
      {
        type: 'p',
        text:
          'Most founders don’t think about shipping zones until the first month’s shipping bill comes in higher than projected. Then they think about it a lot. The good news is that zones aren’t mysterious; they’re a fixed grid that every major carrier (UPS, FedEx, USPS) uses to price ground service. Once you understand them, every shipping decision gets easier.',
      },
      { type: 'h2', text: 'What a zone actually is' },
      {
        type: 'p',
        children: (
          <>
            A zone is a band of distance from your origin ZIP code. Carriers
            divide the country into 8 ground zones. Zone 2 is closest (same or
            adjacent ZIP region), Zone 8 is the farthest (cross-country). Same
            carrier, same package, same weight — the price changes only
            because the destination is in a different zone.{' '}
            <Ext href="https://www.usps.com/business/prices.htm">USPS</Ext>,{' '}
            <Ext href="https://www.ups.com/">
              UPS
            </Ext>
            , and FedEx all publish their zone charts; the math is identical.
          </>
        ),
      },
      { type: 'h2', text: 'Why zones aren’t universal' },
      {
        type: 'p',
        text:
          'Zones are calculated from the warehouse’s ZIP code, not yours and not the customer’s. Move the origin and every zone shifts. A package going to Atlanta is Zone 2 from Miami, Zone 4 from Dallas, and Zone 5 from Chicago. The carrier doesn’t care which is which; the price card does.',
      },
      {
        type: 'p',
        text: 'This is why “where is your 3PL” is a real strategic question, not a logistics-nerd one. The wrong origin can quietly add 15–25% to your average shipping cost, every order, forever.',
      },
      cta(
        'See exactly what your customers’ transit time looks like from our dock.',
        'Open Zone Map',
        '/zones'
      ),
      { type: 'h2', text: 'The math: zone × weight × dim factor' },
      {
        type: 'p',
        text: 'Three numbers determine the price of any ground parcel:',
      },
      {
        type: 'ol',
        items: [
          'Zone — the distance band, 2 through 8.',
          'Billable weight — the larger of the actual weight and the dimensional weight (length × width × height divided by a dim factor, currently 139 for most domestic ground).',
          'Service level — ground vs expedited.',
        ],
      },
      {
        type: 'p',
        text:
          'You can’t change the customer’s ZIP. You can’t change carrier rate cards. The two things you can change are your origin and your packaging.',
      },
      { type: 'h2', text: 'How to think about origin' },
      {
        type: 'p',
        children: (
          <>
            For most US D2C brands, two origins cover ~80% of the population in
            3 days or less: a Northeast/Mid-Atlantic node, or a sweet-spot
            Southeast node. Our warehouse sits in <strong>Weston, FL</strong>,
            which puts the entire Eastern Seaboard — where the bulk of US
            ecommerce volume lives — in Zones 2–6 with one origin.
            That’s the difference between an average $9 ship and an
            average $12 ship over a year.
          </>
        ),
      },
      { type: 'h2', text: 'What to do about it this week' },
      {
        type: 'ul',
        items: [
          'Run your last 1,000 orders through a zone calculator and check the distribution. If 60%+ of your orders are in Zones 6–8 from your current origin, you’re bleeding margin.',
          'Audit your packaging dimensions. Shaving an inch off a box can drop a unit out of dim-weight pricing entirely.',
          'Negotiate carrier rates if you ship more than 100 orders/day, or partner with a 3PL that already has them.',
        ],
      },
      { type: 'h2', text: 'The bottom line' },
      {
        type: 'p',
        text:
          'Shipping zones are the most boring lever in your P&L and one of the highest-leverage. The brands that actually grow profitably get the origin right, get the packaging right, and stop shipping ground from the wrong coast.',
      },
      cta(
        'Plug your numbers in and see savings in 30 seconds.',
        'Estimate My Savings',
        '/zones#instant-estimate'
      ),
    ],
  },

  {
    slug: 'how-3pl-pricing-works',
    category: 'Pricing',
    date: 'Apr 4, 2026',
    readTime: '9 min read',
    title: 'How 3PL Pricing Actually Works (and Where Fees Hide)',
    excerpt:
      'The cheapest 3PL on paper is rarely the cheapest in practice. Here’s every line item that shows up on a real invoice and the ones that surprise founders the first month.',
    blocks: [
      {
        type: 'p',
        text:
          '3PL pricing isn’t complicated by accident. The more line items there are, the harder it is to compare quotes — and the easier it is for the cheap-on-paper provider to look better than the actually-cheap one. Here’s the structure of a real 3PL invoice and the four places fees love to hide.',
      },
      { type: 'h2', text: 'The five real cost buckets' },
      {
        type: 'ul',
        items: [
          'Receiving — charged when inventory arrives at the warehouse. Usually per pallet, per carton, or per labor hour.',
          'Storage — charged monthly. Usually per pallet, per shelf, or per cubic foot.',
          'Pick & pack — charged per outbound order, sometimes with extra fees per additional unit.',
          'Shipping — the actual carrier cost (UPS / FedEx / USPS), passed through to you.',
          'Returns — charged per inbound return, sometimes with extra inspection or restock fees.',
        ],
      },
      {
        type: 'p',
        text:
          'Everything else (kitting, special projects, account management, software, etc.) is layered on top.',
      },
      { type: 'h2', text: 'Pick & pack: the headline number' },
      {
        type: 'p',
        children: (
          <>
            Most 3PLs lead with their pick fee because it’s the most
            visible. Industry typical is around{' '}
            <strong>$2.50–$3.50 for the first item</strong>, plus
            $0.20–$0.30 for each additional item in the order. A 4-item
            order at the typical 3PL is around $3.50. We charge a flat $2 with{' '}
            <strong>free additional picks</strong>. Multiplied across 5,000
            orders a month, the difference is real money.
          </>
        ),
      },
      { type: 'h2', text: 'Storage: the prorated lie' },
      {
        type: 'p',
        text:
          'Most 3PLs charge for the pallet position you’re assigned, not the pallet space you’re using. So if your inventory only fills a third of the pallet, you’re paying for the empty two-thirds. We charge $25 per pallet, prorated to actual utilization — you only pay for the cubic feet your products occupy.',
      },
      cta(
        'See our flat-rate pick fee + prorated storage at a glance.',
        'View Pricing',
        '/#pricing'
      ),
      { type: 'h2', text: 'The four hidden fees to ask about up front' },
      {
        type: 'ul',
        items: [
          '“Account management” / “client services” monthly fees — a sneaky $200–$500/month that often shows up after onboarding.',
          'Per-SKU storage minimums — some 3PLs bill a minimum dollar amount per SKU stored even if the SKU is tiny.',
          'Inbound discrepancy fees — small per-line charges every time their count differs from your packing slip. Asks like nothing, scales like a lot.',
          'Software fees — monthly platform charges that look small until you compare two providers’ all-in totals.',
        ],
      },
      {
        type: 'p',
        children: (
          <>
            None of these are inherently wrong, but a transparent 3PL discloses
            them in the pricing conversation, not in the second invoice. If a
            quote doesn’t add up to a clear per-order all-in number, ask
            for a sample monthly invoice based on{' '}
            <strong>your actual numbers</strong>. The answer will tell you
            everything.
          </>
        ),
      },
      { type: 'h2', text: 'How to compare quotes apples-to-apples' },
      {
        type: 'p',
        text:
          'Don’t compare pick fees. Compare effective per-order all-in cost. Take a representative month of yours — say, 2,500 orders averaging 1.5 items, 4 pallets of storage, 12 returns — and ask each 3PL to model it. The provider that responds in two days with a clean number is the one to keep talking to.',
      },
      { type: 'h2', text: 'The bottom line' },
      {
        type: 'p',
        text:
          'Cheap pick fees + opaque everything else is the oldest trick in 3PL sales. Read the whole rate card. Ask for a sample invoice. Compare on total monthly cost, not headline rates. The right partner will make that easy.',
      },
      cta(
        'Want a real, modeled estimate for your store — in 30 seconds?',
        'Estimate My Savings',
        '/zones#instant-estimate'
      ),
    ],
  },

  {
    slug: '3pl-onboarding-checklist',
    category: 'Operations',
    date: 'Apr 22, 2026',
    readTime: '8 min read',
    title: 'The 3PL Onboarding Checklist: What to Do Before Your First Shipment',
    excerpt:
      'Onboarding goes well or badly based on what happens before the first pallet ships. Here’s the checklist that keeps the cutover boring — and that’s exactly the goal.',
    blocks: [
      {
        type: 'p',
        text:
          'A great cutover is a non-event. Orders shipped Tuesday from the old warehouse and Wednesday from the new one. Customers didn’t notice. The team didn’t panic. That outcome is 90% preparation and 10% execution. Run the checklist below and you’ll be on the calm side.',
      },
      { type: 'h2', text: 'Two weeks before cutover' },
      {
        type: 'ul',
        items: [
          'Lock the SKU master file. Every SKU you ship needs a unique code, a description, weight, dimensions, and product photo. No “miscellaneous” SKUs, no duplicates.',
          'Decide what stays and what goes. The new warehouse is the chance to clean dead inventory — not move it.',
          'Pick the integration cutover date and freeze nonessential storefront changes (theme launches, big sales) for that week.',
          'Brief your customer support team on the changeover. Have a canned response ready in case anything ships late.',
        ],
      },
      { type: 'h2', text: 'One week before cutover' },
      {
        type: 'ul',
        items: [
          'Connect the storefront to the new 3PL’s system. Run a few test orders end-to-end. Make sure tracking syncs back to your storefront.',
          'Confirm carrier accounts: are you shipping on the 3PL’s rates, or on your own carrier accounts billed to you directly?',
          'Send the first inbound shipment with a packing list that matches your SKU master.',
          'Walk through your packing requirements with the 3PL. Inserts, tissue, stickers, branded tape — photograph the ideal package.',
        ],
      },
      cta(
        'See what a transparent, no-minimum 3PL onboarding looks like.',
        'View Pricing',
        '/#pricing'
      ),
      { type: 'h2', text: 'The day of cutover' },
      {
        type: 'ul',
        items: [
          'Old 3PL ships its last open orders.',
          'Storefront flips to the new 3PL as the assigned location.',
          'New 3PL ships any orders that came in after the flip.',
          'Customer support is on standby in case any orders slip the gap.',
        ],
      },
      { type: 'h2', text: 'Week one: don’t go quiet' },
      {
        type: 'p',
        text:
          'The first week is when small misalignments compound. Pull cycle counts on your top 20 SKUs, audit a few packing photos, and track on-time ship rates daily. Anything off should get a same-day fix — not a “we’ll look at it Friday.”',
      },
      {
        type: 'p',
        text: 'The right 3PL will welcome the scrutiny. The wrong one will resist it.',
      },
      { type: 'h2', text: 'The bottom line' },
      {
        type: 'p',
        children: (
          <>
            If onboarding feels chaotic, the next year will too. The way a 3PL
            handles your first 30 days is a tell for how they’ll handle
            your business going forward. Spend the time on data prep, test
            orders, and reconciliation up front and you get a clean cutover
            and a partner that runs in the background.{' '}
            <Int to="/faq">Read our onboarding FAQ</Int> for more.
          </>
        ),
      },
      cta(
        'Want to see what your store would actually save?',
        'Estimate My Savings',
        '/zones#instant-estimate'
      ),
    ],
  },

  {
    slug: 'custom-packaging-roi',
    category: 'Brand',
    date: 'May 1, 2026',
    readTime: '7 min read',
    title: 'Custom Packaging That Actually Pays Off (Without Killing Your Margin)',
    excerpt:
      'Branded boxes feel premium. They also kill margin if you do them wrong. Here’s where to invest in packaging — and where to keep the plain mailer.',
    blocks: [
      {
        type: 'p',
        text:
          'There’s a moment in every brand’s life when the founder sees a competitor’s unboxing on Instagram and decides we need that. Sometimes that decision is right. Often it’s how a healthy brand starts subsidizing a packaging line item it can’t afford. Here’s how to think about packaging investment without lighting cash on fire.',
      },
      { type: 'h2', text: 'Why custom packaging matters' },
      {
        type: 'p',
        text:
          'The unboxing is your last touchpoint before the customer becomes either a repeat buyer or a one-and-done. It’s also the most photographable moment in the entire customer journey — free organic content, every order. Done right, packaging earns its line item back through retention and UGC. Done wrong, it’s a $1.20-per-order tax with no measurable lift.',
      },
      { type: 'h2', text: 'The three places to invest first' },
      {
        type: 'ol',
        items: [
          'A box or mailer that fits the product. A custom mailer in your brand color is 10x more impactful than a luxe box that’s the wrong size for the SKU.',
          'A simple insert card. Thank-you note, care instructions, or a discount for next order. Cheap to print, high signal.',
          'Branded tape. The single highest-impact upgrade per dollar spent. Turns a plain brown box into your box.',
        ],
      },
      { type: 'h2', text: 'The three places NOT to invest yet' },
      {
        type: 'ol',
        items: [
          'Custom-printed tissue paper. Beautiful, expensive, almost never noticed by the customer.',
          'Letterpress / foil-stamped boxes for SKUs under $30. The math doesn’t work.',
          'Heavy-grade boxes when the product doesn’t need protection. Adds shipping weight and dim-weight penalties for no functional benefit.',
        ],
      },
      cta(
        'Want a fulfillment partner that treats packaging like it’s the brand?',
        'Book a Call',
        '/#contact'
      ),
      { type: 'h2', text: 'Working with a 3PL on packaging' },
      {
        type: 'p',
        text: 'A few practical pieces:',
      },
      {
        type: 'ul',
        items: [
          'Send a packing-spec photo. Not a paragraph — a photo of the ideal box, exactly how you want it sent. Pin it in your 3PL’s system.',
          'Pre-build any GWP or kitting whenever possible. Doing it at the pick station every order is the most expensive way to assemble.',
          'Audit the packing weekly. Ask your 3PL to share a few random photos. Quality drift is real, but easy to catch.',
        ],
      },
      { type: 'h2', text: 'The bottom line' },
      {
        type: 'p',
        text:
          'Spend the packaging budget on what the customer actually sees in the first ten seconds: the box’s color, a small insert, branded tape. Skip the stuff they’ll throw out. And work with a 3PL that understands the unboxing is the close — not a back-office step.',
      },
      cta(
        'Get a real estimate for your brand — packaging included.',
        'Estimate My Savings',
        '/zones#instant-estimate'
      ),
    ],
  },

  {
    slug: 'when-to-switch-3pls',
    category: 'Operations',
    date: 'May 5, 2026',
    readTime: '8 min read',
    title: 'When to Switch 3PLs (and When to Just Have a Hard Conversation)',
    excerpt:
      'Switching 3PLs is painful. Staying with the wrong one is worse. Here’s how to tell which problems are fixable and which ones mean it’s time to leave.',
    blocks: [
      {
        type: 'p',
        text:
          'Switching 3PLs is one of the most disruptive operational moves a brand can make. It’s also occasionally the highest-leverage one. The trick is knowing the difference between problems that get fixed with a phone call and problems that won’t fix at all.',
      },
      { type: 'h2', text: 'Problems that are usually fixable' },
      {
        type: 'ul',
        items: [
          'Slow ship times during peak. Often a staffing issue your 3PL can solve if you flag it 60 days in advance.',
          'Packing inconsistency. Send a photo spec and ask for weekly QA samples.',
          'Slow email responses. Ask for a direct contact, not a shared inbox.',
          'Inventory accuracy drift. Schedule a cycle count and audit the top 50 SKUs together.',
        ],
      },
      {
        type: 'p',
        text: 'These are signs of capacity, communication, or process gaps — not character flaws. A real partner will fix them when you raise them clearly.',
      },
      { type: 'h2', text: 'Problems that probably aren’t fixable' },
      {
        type: 'ul',
        items: [
          'Surprise fees on every invoice that you have to dispute. Pricing is character; you can’t un-fix that.',
          'Damaged inventory with no accountability. If they’re losing units and shrugging, they’ll keep shrugging.',
          'No visibility into what’s in your account. If you can’t pull live inventory, you’re flying blind.',
          'Account manager turnover every quarter. The system doesn’t know you, and it never will.',
        ],
      },
      cta(
        'See what transparent, founder-led fulfillment looks like.',
        'View Pricing',
        '/#pricing'
      ),
      { type: 'h2', text: 'The hard conversation framework' },
      {
        type: 'p',
        text:
          'Before you walk, run the conversation once. Not a vague “we’re unhappy” email — a structured one. It buys you either a real fix or a real signal that they can’t deliver.',
      },
      {
        type: 'ol',
        items: [
          'List the top three issues with specific examples and dates.',
          'State the impact (orders late, customer tickets, dollars).',
          'Ask for an action plan with owners and dates.',
          'Set a follow-up 30 days out.',
        ],
      },
      {
        type: 'p',
        text:
          'If the response is defensive, generic, or never comes — you have your answer. If it’s specific, accountable, and actually fixed by the 30-day check-in, you just saved yourself a switch.',
      },
      { type: 'h2', text: 'When to actually switch' },
      {
        type: 'p',
        text:
          'You switch when the math is clear: the cost of staying (lost customers, internal time, dollar leakage) exceeds the cost of moving (cutover risk, ramp time). For most growing brands, that crossover happens earlier than they think — usually around the third major issue that doesn’t get resolved.',
      },
      { type: 'h2', text: 'The bottom line' },
      {
        type: 'p',
        children: (
          <>
            A great 3PL is invisible. A bad one is a constant tax on your time
            and your customer experience. If you’re reading this, you
            probably already know which one you have. The only question left is
            whether the next one will be different. Read our{' '}
            <Int to="/faq">FAQ</Int>, look at the{' '}
            <Int to="/zones">zone math</Int>, and{' '}
            <Int to="/#contact">talk to us</Int>. Worst case, you get a free
            estimate.
          </>
        ),
      },
      cta(
        'Get a free, no-pressure estimate in 30 seconds. No call required.',
        'Estimate My Savings',
        '/zones#instant-estimate'
      ),
    ],
  },
];

export const ARTICLES_BY_SLUG = Object.fromEntries(
  ARTICLES.map((a) => [a.slug, a])
);
